import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { BatteryWarning, ChevronRight, RefreshCw, ShieldCheck } from 'lucide-react';

import { loadAllMobileAccounts, loadMobileView } from './api';
import { Empty, Failed, Head, Loading, Pill, SearchField, WorkspaceTabs } from './parts';
import { tx } from './i18n';
import { useLiveResource } from './useLive';
import { useAppStrings } from './AppPhone';
import DeviceHealth, { HealthUnavailable } from './DeviceHealth';
import { DeviceArt, FINISHES, RingArt, deviceLook, finishLabel } from './DeviceArt';
import {
  BatteryMark, CHART, COMPACT, ChartTip, KpiRow, NUMBER, Panel, PlaceFilters, PlatformMark, RangeTabs, ago, averageOf, counted, hourly, originLabel, placeMatches,
  percent, pointLabel, span, stackRows, tipPoint, useNow, versusAverage,
} from './MobileParts';

const ORIGIN_COLORS = { r01: CHART.ink, b01: CHART.mid, apple: CHART.soft, android: '#c9d1e6', manual: CHART.pale };
const OUTCOMES = { success: CHART.ink, timeout: CHART.soft, error: '#d9957f', aborted: CHART.pale };
const DAY_MS = 86400000;
const ROSTER_PAGE = 25;

function reasonLabel(name) {
  const known = ['server', 'bgtask', 'bgtask_processing', 'push', 'push_headless', 'quick_retry', 'pair', 'manual', 'unreported',
    'background', 'foreground', 'ios', 'android', 'r01', 'b01', 'unknown'];
  return known.includes(name) ? tx(`ui.mobile.sync_reason_${name}`) : name;
}

function freshness(lastSuccessAt, now) {
  if (lastSuccessAt === null || lastSuccessAt === undefined) return 'none';
  const age = now - lastSuccessAt;
  if (age < DAY_MS) return 'today';
  if (age < 7 * DAY_MS) return 'week';
  return 'older';
}

const FRESHNESS = ['today', 'week', 'older', 'none'];

// How recently each device's data came in, as one bar: today, this week, longer ago, never.
function FreshnessBar({ devices, now }) {
  const counts = FRESHNESS.map((level) => devices.filter((device) => freshness(device.lastSuccessAt, now) === level).length);
  const total = Math.max(1, devices.length);
  return (
    <div className="xo-mob-freshness">
      <span className="xo-mob-freshness-bar" aria-hidden="true">
        {FRESHNESS.map((level, index) => counts[index] > 0 && <i key={level} className={`is-${level}`} style={{ flexGrow: counts[index] / total }} />)}
      </span>
      <span className="xo-mob-freshness-key">
        {FRESHNESS.map((level, index) => (
          <span key={level}><i className={`is-${level}`} />{tx(`ui.mobile.fresh_${level}`)}<b>{NUMBER.format(counts[index])}</b></span>
        ))}
      </span>
    </div>
  );
}

// The rings people wear, by finish, from the names the rings advertise.
function Finishes({ rings, as }) {
  const known = FINISHES.map((entry) => ({ ...entry, count: rings.filter((ring) => deviceLook('r01', ring.name).finish === entry.finish && deviceLook('r01', ring.name).known).length }))
    .filter((entry) => entry.count > 0).sort((a, b) => b.count - a.count);
  const unknown = rings.filter((ring) => !deviceLook('r01', ring.name).known).length;
  return (
    <div className="xo-mob-finishes">
      <ul>
        {known.map((entry) => (
          <li key={entry.finish} title={`${as(entry.material)} · ${as(entry.color)}`}>
            <RingArt finish={entry.finish} size={30} />
            <span><strong>{as(entry.color)}</strong><small>{as(entry.material)}</small></span>
            <b>{NUMBER.format(entry.count)}</b>
          </li>
        ))}
        {unknown > 0 && <li className="is-unknown"><span><strong>{tx('ui.mobile.finish_unreported')}</strong><small>{tx('ui.mobile.finish_unreported_detail')}</small></span><b>{NUMBER.format(unknown)}</b></li>}
      </ul>
    </div>
  );
}

function Fleet({ data, now }) {
  const count = (origin, status) => data.fleet.filter((row) => row.origin === origin && row.status === status).reduce((sum, row) => sum + row.devices, 0);
  return (
    <div className="xo-mob-fleet">
      {['r01', 'b01'].map((origin) => {
        const owners = data.owners.filter((row) => row.origin === origin);
        const devices = data.devices.filter((device) => device.origin === origin);
        const on = (platform) => owners.filter((row) => row.platform === platform).reduce((sum, row) => sum + row.devices, 0);
        // The ring on the card wears the finish most people have, once most rings have said
        // which they are; until then it is the app's own silver.
        const worn = origin === 'r01' ? devices.map((device) => deviceLook('r01', device.name)).filter((look) => look.known) : [];
        const common = worn.length === 0 || worn.length * 2 < devices.length ? null : FINISHES.map((entry) => entry.finish)
          .sort((a, b) => worn.filter((look) => look.finish === b).length - worn.filter((look) => look.finish === a).length)[0];
        return (
          <article key={origin} className={`xo-mob-product is-${origin}`}>
            <div className="xo-mob-product-art">
              {origin === 'r01' ? <RingArt finish={common || 'metal_silver'} size={150} /> : <DeviceArt origin="b01" size={150} />}
            </div>
            <div className="xo-mob-product-body">
              <span className="xo-mob-product-name">{originLabel(origin)}</span>
              <strong>{NUMBER.format(count(origin, 'paired'))}</strong>
              <span className="xo-mob-product-sub">{tx('ui.mobile.paired_forgotten', { forgotten: NUMBER.format(count(origin, 'forgotten')) })}</span>
              <dl>
                <div><dt><PlatformMark platform="ios" withLabel /></dt><dd>{NUMBER.format(on('ios'))}</dd></div>
                <div><dt><PlatformMark platform="android" withLabel /></dt><dd>{NUMBER.format(on('android'))}</dd></div>
                <div><dt>{tx('ui.mobile.wearers')}</dt><dd>{NUMBER.format(new Set(devices.map((device) => device.accountUuid)).size)}</dd></div>
              </dl>
              <FreshnessBar devices={devices} now={now} />
            </div>
          </article>
        );
      })}
    </div>
  );
}

// Wearables running low, emptiest first, so support can nudge the person before the data stops.
function LowBattery({ devices, now, mayReadHealth }) {
  const low = devices.filter((device) => device.battery !== null && device.battery !== undefined && device.battery <= 20 && !device.charging)
    .sort((a, b) => a.battery - b.battery).slice(0, 7);
  if (low.length === 0) return <Empty title={tx('ui.mobile.no_low_battery')} detail={tx('ui.mobile.no_low_battery_detail')} />;
  return (
    <ol className="xo-mob-low">
      {low.map((device) => (
        <li key={device.id}>
          <Link to={mayReadHealth ? `/xomics/mobile/wearables?device=${device.id}` : `/xomics/mobile/accounts?account=${device.accountUuid}`}>
            <DeviceArt origin={device.origin} name={device.name} size={34} />
            <span className="xo-mob-low-main"><strong>{device.owner || device.email}</strong><small>{originLabel(device.origin)} · {device.batteryUpdatedAt ? ago(device.batteryUpdatedAt, now) : '—'}</small></span>
            <BatteryMark value={device.battery} charging={device.charging} />
          </Link>
        </li>
      ))}
    </ol>
  );
}

function Arriving({ ingestions, step }) {
  const series = useMemo(() => {
    const byDay = new Map();
    for (const entry of ingestions) {
      if (!byDay.has(entry.at)) byDay.set(entry.at, { at: entry.at });
      byDay.get(entry.at)[entry.origin] = entry.submissions;
    }
    return [...byDay.values()].sort((a, b) => a.at - b.at);
  }, [ingestions]);
  const origins = useMemo(() => [...new Set(ingestions.map((entry) => entry.origin))], [ingestions]);
  const total = (point) => origins.reduce((sum, origin) => sum + (point[origin] || 0), 0);
  const averageTotal = averageOf(series.map(total));
  if (series.length === 0) return <Empty title={tx('ui.mobile.no_uploads')} />;
  const parts = origins.map((origin) => ({ key: origin, name: originLabel(origin), color: ORIGIN_COLORS[origin] || CHART.soft }));
  return (
    <div className="xo-mob-chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={CHART.grid} />
          <XAxis dataKey="at" tickFormatter={pointLabel(step)} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={40} tickFormatter={(value) => COMPACT.format(value)} />
          <Tooltip cursor={CHART.lineCursor} animationDuration={120} content={(
            <ChartTip title={tipPoint(step)} rows={(point) => stackRows(point, parts, { sorted: true })}
              facts={(point) => [
                { label: tx('ui.mobile.tip_uploads_total'), value: NUMBER.format(total(point)) },
                versusAverage(total(point), averageTotal),
              ]} />
          )} />
          {origins.map((origin) => (
            <Area key={origin} type="monotone" dataKey={origin} stackId="o" name={originLabel(origin)}
              stroke={ORIGIN_COLORS[origin] || CHART.soft} fill={ORIGIN_COLORS[origin] || CHART.soft} fillOpacity={0.2} strokeWidth={1.8} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
      <div className="xo-mob-key">{origins.map((origin) => <span key={origin}><i style={{ background: ORIGIN_COLORS[origin] || CHART.soft }} />{originLabel(origin)}</span>)}</div>
    </div>
  );
}

// wearerPlaces: each wearer's country and city from the account list, by account uuid.
function Roster({ devices, wearerPlaces, diagnostics, mayReadHealth, now, as }) {
  const [view, setView] = useState('all');
  const [search, setSearch] = useState('');
  const [place, setPlace] = useState({ country: '', city: '' });
  const [expanded, setExpanded] = useState(false);
  const term = search.trim().toLowerCase();
  const placeOf = (device) => wearerPlaces.get(device.accountUuid) || {};
  const matches = (device) => {
    if (term && ![device.owner, device.email, device.macTail].some((value) => typeof value === 'string' && value.toLowerCase().includes(term))) return false;
    if (!placeMatches(placeOf(device), place.country, place.city)) return false;
    if (view === 'r01' || view === 'b01') return device.origin === view;
    if (view === 'today') return freshness(device.lastSuccessAt, now) === 'today';
    if (view === 'quiet') return freshness(device.lastSuccessAt, now) !== 'today';
    return true;
  };
  const shown = devices.filter(matches);
  return (
    <>
      <div className="xo-mob-roster-bar">
        <div className="xo-mob-roster-find">
          <SearchField value={search} onChange={setSearch} placeholder={tx('ui.mobile.search_wearers')} className="xo-mob-roster-search" autoComplete="off" />
          <PlaceFilters places={devices.map(placeOf)} country={place.country} city={place.city} onChange={setPlace} />
        </div>
        <WorkspaceTabs value={view} onChange={setView} label={tx('ui.mobile.roster_filter')} className="is-compact xo-mob-tabs" tabs={[
          { value: 'all', label: tx('ui.mobile.all_devices'), count: devices.length },
          { value: 'r01', label: tx('ui.mobile.origin_r01'), count: devices.filter((device) => device.origin === 'r01').length },
          { value: 'b01', label: tx('ui.mobile.origin_b01'), count: devices.filter((device) => device.origin === 'b01').length },
          { value: 'today', label: tx('ui.mobile.synced_today'), count: devices.filter((device) => freshness(device.lastSuccessAt, now) === 'today').length },
          { value: 'quiet', label: tx('ui.mobile.not_today'), count: devices.filter((device) => freshness(device.lastSuccessAt, now) !== 'today').length },
        ]} />
      </div>
      <div className={`xo-mob-table${diagnostics ? ' has-diagnostics' : ''}`} role="table">
        <div className="xo-mob-table-head" role="row">
          <span>{tx('ui.mobile.device')}</span><span>{tx('ui.mobile.worn_by')}</span><span>{tx('ui.mobile.device_paired')}</span>
          <span>{tx('ui.mobile.last_synced')}</span>{diagnostics && <span>{tx('ui.mobile.sync_success_rate')}</span>}<span />
        </div>
        {shown.length === 0 ? <Empty title={tx('ui.mobile.no_devices_match')} /> : shown.slice(0, expanded ? shown.length : ROSTER_PAGE).map((device) => {
          const fresh = freshness(device.lastSuccessAt, now);
          const total = diagnostics ? device.syncs.success + device.syncs.timeout + device.syncs.error + device.syncs.aborted : 0;
          const target = mayReadHealth ? `/xomics/mobile/wearables?device=${device.id}` : `/xomics/mobile/accounts?account=${device.accountUuid}`;
          const finish = finishLabel(deviceLook(device.origin, device.name), as);
          return (
            <Link key={device.id} className="xo-mob-table-row" role="row" to={target}>
              <span className="xo-mob-cell-device">
                <DeviceArt origin={device.origin} name={device.name} size={36} />
                <span><strong>{originLabel(device.origin)}</strong><small>{[finish, device.macTail ? `…${device.macTail}` : null].filter(Boolean).join(' · ')}</small></span>
                {device.battery !== null && device.battery !== undefined && <BatteryMark value={device.battery} charging={device.charging} />}
              </span>
              <span className="xo-mob-cell-person"><strong>{device.owner || device.email}</strong><small>{device.owner ? device.email : ''}</small></span>
              <span className="xo-mob-cell-muted">{device.pairedAt ? ago(device.pairedAt, now) : '—'}</span>
              <span className={`xo-mob-cell-fresh is-${fresh}`}><i aria-hidden="true" />{device.lastSuccessAt ? ago(device.lastSuccessAt, now) : tx('ui.mobile.not_synced_yet')}</span>
              {diagnostics && (
                <span className="xo-mob-cell-rate" title={tx('ui.mobile.sync_split_range')}>
                  <span className="xo-mob-outcome-bar">
                    {['success', 'timeout', 'error'].map((outcome) => (
                      <i key={outcome} style={{ width: `${((outcome === 'error' ? device.syncs.error + device.syncs.aborted : device.syncs[outcome]) / Math.max(1, total)) * 100}%`, background: OUTCOMES[outcome] }} />
                    ))}
                  </span>
                  <small>{total === 0 ? '—' : `${percent(device.syncs.success, total)} · ${NUMBER.format(total)}`}</small>
                </span>
              )}
              <ChevronRight aria-hidden="true" />
            </Link>
          );
        })}
      </div>
      {shown.length > ROSTER_PAGE && (
        <button type="button" className="xo-mob-more" onClick={() => setExpanded(!expanded)}>
          {expanded ? tx('ui.mobile.show_fewer') : tx('ui.mobile.show_all_devices', { count: NUMBER.format(shown.length) })}
        </button>
      )}
    </>
  );
}

// Syncs from builds that never reported the split go last: they say nothing about the split itself.
function OutcomeTable({ rows }) {
  if (rows.length === 0) return <Empty title={tx('ui.mobile.no_syncs')} />;
  const ordered = [...rows].sort((a, b) => Number(a.name === 'unreported') - Number(b.name === 'unreported'));
  return (
    <ol className="xo-mob-outcomes">
      {ordered.map((row) => (
        <li key={row.name}>
          <span><strong>{reasonLabel(row.name)}</strong><small>{counted('ui.mobile.outcome_meta', row.total, { median: span(row.medianMs) })}</small></span>
          <span className="xo-mob-outcome-bar">
            <i style={{ width: `${(row.success / Math.max(1, row.total)) * 100}%`, background: OUTCOMES.success }} />
            <i style={{ width: `${(row.timeout / Math.max(1, row.total)) * 100}%`, background: OUTCOMES.timeout }} />
            <i style={{ width: `${((row.error + row.aborted) / Math.max(1, row.total)) * 100}%`, background: OUTCOMES.error }} />
          </span>
          <em>{percent(row.success, row.total)}</em>
        </li>
      ))}
    </ol>
  );
}

const PHASES = [
  ['connectMs', 'ui.mobile.phase_connect', CHART.deep], ['infoMs', 'ui.mobile.phase_info', '#203a85'],
  ['healthDataMs', 'ui.mobile.phase_health', '#435ba1'], ['uploadMs', 'ui.mobile.phase_upload', CHART.mid],
  ['clearMs', 'ui.mobile.phase_clear', CHART.soft], ['initMs', 'ui.mobile.phase_init', CHART.pale],
];

function Anatomy({ phases }) {
  const groups = [
    ['all', 'ui.mobile.anatomy_all'], ['ios', 'ui.mobile.anatomy_ios'], ['android', 'ui.mobile.anatomy_android'],
    ['r01', 'ui.mobile.anatomy_r01'], ['b01', 'ui.mobile.anatomy_b01'],
  ].filter(([key]) => phases[key] && PHASES.some(([phase]) => phases[key][phase].samples > 0));
  if (groups.length === 0) return <Empty title={tx('ui.mobile.no_timings')} detail={tx('ui.mobile.no_timings_detail')} />;
  const totalOf = (key) => PHASES.reduce((sum, [phase]) => sum + (phases[key][phase].median || 0), 0);
  const longest = Math.max(1, ...groups.map(([key]) => totalOf(key)));
  return (
    <div className="xo-mob-anatomy">
      {groups.map(([key, label]) => (
        <div key={key} className="xo-mob-anatomy-row">
          <span className="xo-mob-anatomy-label"><strong>{tx(label)}</strong><small>{counted('ui.mobile.samples', phases[key].connectMs.samples)}</small></span>
          <div className="xo-mob-anatomy-bar" style={{ width: `${(totalOf(key) / longest) * 100}%` }}>
            {PHASES.map(([phase, phaseLabel, color]) => {
              const value = phases[key][phase].median || 0;
              return value > 0 && (
                <i key={phase} style={{ background: color, flexGrow: value }} title={`${tx(phaseLabel)} ${span(value)}`}>
                  {value / longest >= 0.1 && <b>{span(value)}</b>}
                </i>
              );
            })}
          </div>
          <em>{span(totalOf(key))}</em>
        </div>
      ))}
      <div className="xo-mob-key">{PHASES.map(([phase, label, color]) => <span key={phase}><i style={{ background: color }} />{tx(label)}</span>)}</div>
    </div>
  );
}

// Admin+ only, and folded away: how the syncs behind the fleet went.
function Diagnostics({ syncs, step }) {
  const totals = syncs.totals;
  const terminal = totals.success + totals.timeout + totals.error + totals.aborted;
  const typical = syncs.byOrigin.reduce((best, row) => (row.medianMs !== null && row.success > (best ? best.success : -1) ? row : best), null);
  return (
    <details className="xo-mob-diagnostics">
      <summary>
        <ShieldCheck aria-hidden="true" />
        <span><strong>{tx('ui.mobile.sync_diagnostics')}</strong><small>{tx('ui.mobile.sync_diagnostics_detail')}</small></span>
        <Pill tone="flat">{tx('ui.mobile.admin_plus_only')}</Pill>
      </summary>
      <div className="xo-mob-diagnostics-body">
        <div className="xo-mob-diag-facts">
          <span><small>{tx('ui.mobile.sync_success_rate')}</small><strong>{percent(totals.success, terminal)}</strong><em>{counted('ui.mobile.of_syncs', terminal, {}, COMPACT)}</em></span>
          <span><small>{tx('ui.mobile.typical_sync')}</small><strong>{span(typical ? typical.medianMs : null)}</strong><em>{tx('ui.mobile.typical_sync_detail')}</em></span>
          <span><small>{tx('ui.mobile.unfinished_syncs')}</small><strong>{NUMBER.format(terminal - totals.success)}</strong>
            <em>{tx('ui.mobile.unfinished_detail', { timeouts: NUMBER.format(totals.timeout), errors: counted('ui.mobile.errors_count', totals.error + totals.aborted) })}</em></span>
        </div>
        <div className="xo-mob-row is-wide-left">
          <Panel title={tx('ui.mobile.anatomy_title')} detail={tx('ui.mobile.anatomy_detail')}><Anatomy phases={syncs.phases} /></Panel>
          <Panel title={tx('ui.mobile.failures_title')} detail={tx('ui.mobile.failures_detail')}>
            {syncs.errors.length === 0 ? <Empty title={tx('ui.mobile.no_failure_reasons')} /> : (
              <ol className="xo-mob-errors">{syncs.errors.map((entry) => <li key={entry.error}><code>{entry.error}</code><strong>{NUMBER.format(entry.count)}</strong></li>)}</ol>
            )}
          </Panel>
        </div>
        <Panel title={tx('ui.mobile.sync_outcomes')} detail={tx(hourly(step) ? 'ui.mobile.sync_outcomes_fleet_detail_hours' : 'ui.mobile.sync_outcomes_fleet_detail')}>
          <div className="xo-mob-chart is-short">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={syncs.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={CHART.grid} />
                <XAxis dataKey="at" tickFormatter={pointLabel(step)} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={40} tickFormatter={(value) => COMPACT.format(value)} />
                <Tooltip cursor={CHART.cursor} animationDuration={120} content={(
                  <ChartTip title={tipPoint(step)}
                    rows={(point) => stackRows(point, [
                      { key: 'success', name: tx('ui.mobile.sync_success'), color: OUTCOMES.success },
                      { key: 'timeout', name: tx('ui.mobile.sync_timeout'), color: OUTCOMES.timeout },
                      { key: 'error', name: tx('ui.mobile.sync_error'), color: OUTCOMES.error },
                      { key: 'aborted', name: tx('ui.mobile.sync_aborted'), color: OUTCOMES.aborted },
                    ])}
                    facts={(point) => {
                      const all = point.success + point.timeout + point.error + point.aborted;
                      return [
                        { label: tx('ui.mobile.tip_syncs'), value: NUMBER.format(all) },
                        all > 0 ? { label: tx('ui.mobile.tip_success_rate'), value: percent(point.success, all),
                          tone: point.success / all < 0.8 ? 'bad' : null } : null,
                      ];
                    }} />
                )} />
                <Bar dataKey="success" stackId="s" fill={OUTCOMES.success} name={tx('ui.mobile.sync_success')} />
                <Bar dataKey="timeout" stackId="s" fill={OUTCOMES.timeout} name={tx('ui.mobile.sync_timeout')} />
                <Bar dataKey="error" stackId="s" fill={OUTCOMES.error} name={tx('ui.mobile.sync_error')} />
                <Bar dataKey="aborted" stackId="s" fill={OUTCOMES.aborted} name={tx('ui.mobile.sync_aborted')} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <div className="xo-mob-row is-thirds">
          <Panel title={tx('ui.mobile.why_syncs')} detail={tx('ui.mobile.why_syncs_detail')}><OutcomeTable rows={syncs.byReason} /></Panel>
          <Panel title={tx('ui.mobile.by_platform')} detail={tx('ui.mobile.by_platform_detail')}><OutcomeTable rows={syncs.byPlatform} /></Panel>
          <Panel title={tx('ui.mobile.by_device')} detail={tx('ui.mobile.by_device_detail')}>
            <OutcomeTable rows={syncs.byOrigin} />
            <h4 className="xo-mob-subhead">{tx('ui.mobile.app_state')}</h4>
            <OutcomeTable rows={syncs.byTrigger} />
          </Panel>
        </div>
      </div>
    </details>
  );
}

// A device's readings open in a panel over the page, which stays where it was behind it.
function DeviceDrawer({ onClose, children }) {
  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);
  return createPortal(
    <>
      <motion.div className="xo-mob-drawer-backdrop" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
      <motion.aside className="xo-mob-drawer xo-mobile-page" role="dialog" aria-modal="true"
        initial={{ opacity: 0, x: 48 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 48 }} transition={{ duration: 0.22, ease: 'easeOut' }}>
        {children}
      </motion.aside>
    </>,
    document.getElementById('xo') ?? document.body,
  );
}

export default function MobileWearables() {
  const [days, setDays] = useState(30);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const deviceId = searchParams.get('device');
  const load = useCallback(() => loadMobileView('wearables', days), [days]);
  const report = useLiveResource([], load, { reconcileMs: 60000 });
  // The wearers' places, read from the account list, where each account has them.
  const [wearerPlaces, setWearerPlaces] = useState(() => new Map());
  useEffect(() => {
    let live = true;
    loadAllMobileAccounts({ device: 'any' })
      .then((accounts) => { if (live) setWearerPlaces(new Map(accounts.map((account) => [account.uuid, { country: account.country, city: account.city }]))); })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  const now = useNow(30000);
  const as = useAppStrings();
  const closeDevice = useCallback(() => navigate('/xomics/mobile/wearables'), [navigate]);

  if (report.loading && report.data === null) return <Loading label={tx('ui.mobile.reading_wearables')} />;
  if (report.error && report.data === null) return <Failed message={report.error} onRetry={report.refresh} />;
  const data = report.data;
  const openDevice = deviceId !== null && /^[1-9][0-9]*$/.test(deviceId);

  const paired = (origin) => data.fleet.filter((row) => row.origin === origin && row.status === 'paired').reduce((sum, row) => sum + row.devices, 0);
  const wearers = new Set(data.devices.map((device) => device.accountUuid)).size;
  const today = data.devices.filter((device) => freshness(device.lastSuccessAt, now) === 'today').length;
  const week = data.devices.filter((device) => ['today', 'week'].includes(freshness(device.lastSuccessAt, now))).length;
  const uploads = data.ingestions.reduce((sum, entry) => sum + entry.submissions, 0);
  const uploadSeries = dailyUploads(data.ingestions);

  return (
    <div className="xo-platform-page xo-mobile-page">
      <Head title={tx('ui.mobile.wearables_title')} subtitle={tx('ui.mobile.wearables_subtitle')}>
        <RangeTabs value={days} onChange={setDays} />
      </Head>
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}

      <KpiRow items={[
        { label: tx('ui.mobile.paired_rings'), value: NUMBER.format(paired('r01')), detail: counted('ui.mobile.paired_bands_detail', paired('b01')) },
        { label: tx('ui.mobile.wearers'), value: NUMBER.format(wearers), detail: tx('ui.mobile.wearers_detail') },
        { label: tx('ui.mobile.synced_today'), value: NUMBER.format(today), detail: tx('ui.mobile.synced_week_detail', { count: NUMBER.format(week) }) },
        { label: tx('ui.mobile.uploads_in_range'), value: COMPACT.format(uploads), detail: tx('ui.mobile.uploads_in_range_detail'), trend: uploadSeries },
      ]} />

      <Fleet data={data} now={now} />
      {data.devices.some((device) => device.origin === 'r01') && (
        <Panel title={tx('ui.mobile.finishes')} className="xo-mob-finishes-panel">
          <Finishes rings={data.devices.filter((device) => device.origin === 'r01')} as={as} />
        </Panel>
      )}

      <div className="xo-mob-row is-wide-left is-even">
        <Panel title={tx('ui.mobile.data_arriving')} detail={tx(hourly(data.step) ? 'ui.mobile.data_arriving_fleet_detail_hours' : 'ui.mobile.data_arriving_fleet_detail')}>
          <Arriving ingestions={data.ingestions} step={data.step} />
        </Panel>
        <Panel title={tx('ui.mobile.low_battery_title')} detail={tx('ui.mobile.low_battery_detail')}
          aside={<span className="xo-mob-aside-note"><BatteryWarning aria-hidden="true" />{tx('ui.mobile.low_battery_rule')}</span>}>
          <LowBattery devices={data.devices} now={now} mayReadHealth={data.mayReadHealth} />
        </Panel>
      </div>

      <Panel title={tx('ui.mobile.roster_title')}
        detail={data.mayReadHealth ? tx('ui.mobile.roster_detail_health') : tx('ui.mobile.roster_detail')}
        aside={<span className="xo-mob-aside-note"><RefreshCw aria-hidden="true" />{tx('ui.mobile.freshest_first')}</span>}>
        <Roster devices={data.devices} wearerPlaces={wearerPlaces} diagnostics={data.diagnostics} mayReadHealth={data.mayReadHealth} now={now} as={as} />
      </Panel>

      {data.diagnostics && data.syncs && <Diagnostics syncs={data.syncs} step={data.step} />}

      <AnimatePresence>
        {openDevice && (
          <DeviceDrawer key="device" onClose={closeDevice}>
            {data.mayReadHealth ? <DeviceHealth key={deviceId} deviceId={deviceId} onBack={closeDevice} /> : <HealthUnavailable />}
          </DeviceDrawer>
        )}
      </AnimatePresence>
    </div>
  );
}

function dailyUploads(ingestions) {
  const byDay = new Map();
  for (const entry of ingestions) byDay.set(entry.at, (byDay.has(entry.at) ? byDay.get(entry.at) : 0) + entry.submissions);
  return [...byDay.entries()].sort((a, b) => a[0] - b[0]).map(([, value]) => value);
}
